import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import AgePricingEditor from '../restaurant/AgePricingEditor';
import '../restaurant/css/AgePricingEditor.css';
import IngredientsMultiSelect from '../restaurant/IngredientsMultiSelect';
import NutritionEditor, { parseNutritionRowsFromItem } from '../restaurant/NutritionEditor';
import '../restaurant/css/NutritionEditor.css';
import { emptyNutritionRows, buildNutritionForSubmit } from '../restaurant/foodNutrition';
import { resolveIngredientIdsFromMenuItem } from '../restaurant/onlineFoodItems';
import { normalizeIngredientOptions, extractFoodCategoryName, normalizeFoodCategories } from '../restaurant/restaurantApi';
import { processMenuImageFile } from '../restaurant/menuImageUtils';
import '../restaurant/css/Menu.css';
import {
  createJreatsMainMenuItem,
  deactivateJreatsMainMenuItem,
  fetchJreatsMainMenu,
  fetchJreatsMainMenuStates,
  updateJreatsMainMenuItem,
} from './jreatsMainMenuApi';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import './JuniorEatsMainMenuPage.css';
import { confirmAction } from '../restaurant/ui/feedback';

/** Indian states & UTs for Jr Eats statewide menu picker. */
const INDIA_STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

const EMPTY_FORM = () => ({
  foodItem: '',
  foodCategory: '',
  foodType: 'VEG',
  price: '',
  quantity: '',
  notes: '',
  description: '',
  status: 'ACTIVE',
  agePricing: [],
  ingredientIds: [],
  nutrition: emptyNutritionRows(),
  imageFile: null,
  imageUrl: '',
});

function normalizeItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    foodItemId: raw.food_item_id ?? raw.foodItemId,
    foodItem: raw.food_item ?? raw.foodItem ?? '',
    foodCategory: raw.food_category ?? raw.foodCategory ?? '',
    foodType: raw.food_type ?? raw.foodType ?? 'VEG',
    price: raw.price ?? '',
    quantity: raw.quantity ?? '',
    notes: raw.notes ?? '',
    description: raw.description ?? '',
    status: String(raw.status ?? 'ACTIVE').toUpperCase(),
    catalogState: raw.catalog_state ?? raw.catalogState ?? '',
    agePricing: Array.isArray(raw.agePricing)
      ? raw.agePricing
      : Array.isArray(raw.age_pricing)
        ? raw.age_pricing
        : [],
    ingredients: Array.isArray(raw.ingredients) ? raw.ingredients : [],
    ingredientIds: resolveIngredientIdsFromMenuItem(raw),
    nutrition: raw.nutrition ?? raw.Nutrition ?? null,
    calories: raw.calories ?? raw.Calories ?? null,
    imagePath: raw.image_path ?? raw.imagePath ?? '',
    imageUrl: raw.image_url ?? raw.imageUrl ?? '',
  };
}

const JuniorEatsMainMenuPage = () => {
  const [catalogStates, setCatalogStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [showIntroInfo, setShowIntroInfo] = useState(false);
  const introInfoRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  const userId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.userId ?? user?.User_Id ?? user?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const stateOptions = useMemo(() => {
    const fromCatalog = catalogStates
      .map((row) => (row?.state != null ? String(row.state).trim() : ''))
      .filter(Boolean);
    const merged = new Map();
    [...INDIA_STATES, ...fromCatalog].forEach((name) => {
      const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!merged.has(key)) merged.set(key, name);
    });
    return Array.from(merged.values()).sort((a, b) => a.localeCompare(b));
  }, [catalogStates]);

  const loadStates = useCallback(async () => {
    try {
      const rows = await fetchJreatsMainMenuStates();
      setCatalogStates(rows);
      if (!selectedState && rows[0]?.state) {
        setSelectedState(rows[0].state);
      } else if (!selectedState) {
        setSelectedState('Telangana');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load states');
      if (!selectedState) setSelectedState('Telangana');
    }
  }, [selectedState]);

  const loadCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('Token');
      const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantsFoodCategories,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const list = normalizeFoodCategories(res.data);
      const names = [
        ...new Set(
          list
            .map((row) =>
              typeof row === 'string' ? row.trim() : String(extractFoodCategoryName(row) || '').trim(),
            )
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b));
      setCategories(names);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadIngredients = useCallback(async () => {
    try {
      setIngredientsLoading(true);
      const token = localStorage.getItem('Token');
      const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantsIngredients,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setIngredientOptions(normalizeIngredientOptions(res.data?.ingredients));
    } catch {
      setIngredientOptions([]);
    } finally {
      setIngredientsLoading(false);
    }
  }, []);

  const loadItems = useCallback(async (state) => {
    if (!state) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchJreatsMainMenu(state, { includeInactive: true });
      setItems(data.items.map(normalizeItem).filter(Boolean));
      if (data.state) {
        setSelectedState(data.state);
      }
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.error || err?.message || 'Failed to load main menu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStates();
    loadCategories();
    loadIngredients();
  }, [loadStates, loadCategories, loadIngredients]);

  useEffect(() => {
    if (selectedState) loadItems(selectedState);
  }, [selectedState, loadItems]);

  useEffect(() => {
    if (!showIntroInfo) return undefined;
    const handlePointerDown = (event) => {
      if (introInfoRef.current && !introInfoRef.current.contains(event.target)) {
        setShowIntroInfo(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowIntroInfo(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showIntroInfo]);

  useEffect(() => {
    const file = form.imageFile;
    if (!file) {
      setImagePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.imageFile]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM());
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const openEdit = (item) => {
    setEditingId(item.foodItemId);
    setForm({
      foodItem: item.foodItem,
      foodCategory: item.foodCategory,
      foodType: item.foodType || 'VEG',
      price: item.price,
      quantity: item.quantity,
      notes: item.notes,
      description: item.description,
      status: item.status || 'ACTIVE',
      agePricing: item.agePricing || [],
      ingredientIds: Array.isArray(item.ingredientIds)
        ? item.ingredientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : resolveIngredientIdsFromMenuItem(item),
      nutrition: parseNutritionRowsFromItem(item),
      imageFile: null,
      imageUrl: item.imageUrl || '',
    });
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const handleIngredientSelectionChange = (nextSelectedIds) => {
    const normalizedIds = (Array.isArray(nextSelectedIds) ? nextSelectedIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setForm((p) => ({ ...p, ingredientIds: normalizedIds }));
  };

  const handleImageInputChange = async (e) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    try {
      const processed = await processMenuImageFile(file);
      setForm((p) => ({ ...p, imageFile: processed }));
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not process image');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM());
  };

  const handleStateChange = (e) => {
    const next = (e.target.value || '').trim();
    setSelectedState(next);
    setError('');
    setMessage('');
  };

  const saveItem = async (e) => {
    e.preventDefault();
    const state = (selectedState || '').trim();
    if (!state) {
      setError('Select a state first');
      return;
    }
    if (!form.foodItem.trim() || !form.foodCategory.trim() || !form.price || !form.quantity) {
      setError('Food item, category, price, and quantity are required');
      return;
    }
    const ingredientIds = (Array.isArray(form.ingredientIds) ? form.ingredientIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (ingredientIds.length === 0) {
      setError('At least one ingredient is required');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      catalogState: state,
      foodItem: form.foodItem.trim(),
      foodCategory: form.foodCategory.trim(),
      foodType: form.foodType,
      price: String(form.price).trim(),
      quantity: String(form.quantity).trim(),
      notes: form.notes || null,
      description: form.description || null,
      status: form.status,
      agePricing: form.agePricing,
      ingredientIds,
      nutrition: buildNutritionForSubmit(form.nutrition),
      createdById: userId,
      updatedById: userId,
      imageFile: form.imageFile || null,
    };
    try {
      if (editingId) {
        await updateJreatsMainMenuItem(editingId, payload);
        setMessage('Menu item updated');
      } else {
        await createJreatsMainMenuItem(payload);
        setMessage('Menu item created');
      }
      closeForm();
      await loadStates();
      await loadItems(state);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivateItem = async (item) => {
    if (!item?.foodItemId) return;
    const ok = await confirmAction({
      title: 'Deactivate menu item?',
      message: `Deactivate "${item.foodItem}"?`,
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!ok) return;
    setError('');
    try {
      await deactivateJreatsMainMenuItem(item.foodItemId);
      setMessage('Menu item deactivated');
      await loadItems(selectedState);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Deactivate failed');
    }
  };

  return (
    <div className="jreats-main-menu">
      <div className="jreats-main-menu__state-row">
        <label htmlFor="jreats-main-menu-state">State</label>
        <select
          id="jreats-main-menu-state"
          value={selectedState}
          onChange={handleStateChange}
        >
          <option value="">Select state</option>
          {stateOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <div className="jreats-main-menu__info" ref={introInfoRef}>
          <button
            type="button"
            className="jreats-main-menu__info-btn"
            aria-label="About Jr Eats main menu"
            aria-expanded={showIntroInfo}
            aria-controls="jreats-main-menu-info"
            title="About Jr Eats main menu"
            onClick={() => setShowIntroInfo((open) => !open)}
          >
            <FaInfoCircle aria-hidden="true" />
          </button>
          {showIntroInfo ? (
            <div
              id="jreats-main-menu-info"
              className="jreats-main-menu__info-popover"
              role="dialog"
              aria-label="Jr Eats main menu info"
            >
              Statewide Junior Eats main menu by state. Serving restaurants with JREATS
              service use these items for daily menus and templates — they cannot create
              their own Jr Eats main menu.
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--create"
          onClick={openCreate}
          disabled={!selectedState}
        >
          Add item
        </button>
      </div>

      {error ? <p className="jreats-main-menu__error">{error}</p> : null}
      {message ? <p className="jreats-main-menu__message">{message}</p> : null}

      {showForm ? (
        <form className="jreats-main-menu__form" onSubmit={saveItem}>
          <h3>{editingId ? 'Edit menu item' : 'Add menu item'}</h3>
          <div className="jreats-main-menu__form-grid">
            <label>
              Food item
              <input
                value={form.foodItem}
                onChange={(e) => setForm((p) => ({ ...p, foodItem: e.target.value }))}
                required
              />
            </label>
            <label>
              Category
              <select
                value={form.foodCategory}
                onChange={(e) => setForm((p) => ({ ...p, foodCategory: e.target.value }))}
                required
              >
                <option value="">Select food category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                {form.foodCategory &&
                !categories.includes(form.foodCategory) ? (
                  <option value={form.foodCategory}>{form.foodCategory}</option>
                ) : null}
              </select>
            </label>
            <label>
              Food type
              <select
                value={form.foodType}
                onChange={(e) => setForm((p) => ({ ...p, foodType: e.target.value }))}
              >
                <option value="VEG">Veg</option>
                <option value="NON-VEG">Non-Veg</option>
                <option value="EGG">Egg</option>
              </select>
            </label>
            <label>
              Base price
              <input
                value={form.price}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                required
              />
            </label>
            <label>
              Base quantity
              <input
                value={form.quantity}
                onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                required
              />
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>
          </div>
          <label className="jreats-main-menu__full">
            Description
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </label>
          <div className="jreats-main-menu__full jreats-main-menu__image">
            <span className="jreats-main-menu__image-label">Image</span>
            <div className="menu-item-modal-image-field">
              {imagePreviewUrl || form.imageUrl ? (
                <img
                  src={imagePreviewUrl || form.imageUrl}
                  alt={form.foodItem || 'Menu item'}
                  className="menu-item-modal-image-preview"
                />
              ) : null}
              <button
                type="button"
                className="menu-item-modal-file-btn"
                onClick={() => imageFileInputRef.current?.click()}
                disabled={saving}
              >
                Choose File
              </button>
              <span className="menu-item-modal-file-name">
                {form.imageFile?.name || (form.imageUrl ? 'Current image' : 'No file chosen')}
              </span>
              <input
                ref={imageFileInputRef}
                type="file"
                accept="image/*"
                className="menu-item-modal-file-input-hidden"
                aria-label="Choose image file"
                tabIndex={-1}
                onChange={handleImageInputChange}
              />
            </div>
          </div>
          <div className="jreats-main-menu__full jreats-main-menu__ingredients">
            <label htmlFor="jreats-main-menu-ingredients">Ingredients</label>
            <IngredientsMultiSelect
              className="ingredients-dropdown jreats-main-menu__ingredients-select"
              options={ingredientOptions}
              selectedIds={form.ingredientIds}
              onChange={handleIngredientSelectionChange}
              loading={ingredientsLoading}
            />
          </div>
          <AgePricingEditor
            value={form.agePricing}
            onChange={(agePricing) => setForm((p) => ({ ...p, agePricing }))}
            disabled={saving}
          />
          <NutritionEditor
            value={form.nutrition}
            onChange={(nutrition) => setForm((p) => ({ ...p, nutrition }))}
            disabled={saving}
          />
          <div className="jreats-main-menu__form-actions">
            <button type="button" className="eats-admin-btn" onClick={closeForm} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="eats-admin-btn eats-admin-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      ) : null}

      <div className="jreats-main-menu__table-wrap">
        {loading ? (
          <p className="jreats-main-menu__muted">Loading…</p>
        ) : (
          <table className="eats-admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Item</th>
                <th>Category</th>
                <th>Type</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="eats-admin-table__empty">
                    {selectedState
                      ? `No statewide items for ${selectedState}. Add the first item.`
                      : 'Select a state to load the main menu.'}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.foodItemId}>
                    <td>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.foodItem}
                          className="jreats-main-menu__table-thumb"
                        />
                      ) : (
                        <span className="jreats-main-menu__muted">—</span>
                      )}
                    </td>
                    <td>{item.foodItem}</td>
                    <td>{item.foodCategory}</td>
                    <td>{item.foodType}</td>
                    <td>{item.price}</td>
                    <td>{item.quantity}</td>
                    <td>
                      <span
                        className={`eats-admin-status ${
                          item.status === 'ACTIVE'
                            ? 'eats-admin-status--active'
                            : 'eats-admin-status--inactive'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="jreats-main-menu__actions">
                      <button
                        type="button"
                        className="eats-admin-btn eats-admin-btn--secondary"
                        onClick={() => openEdit(item)}
                      >
                        Edit
                      </button>
                      {item.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          className="eats-admin-btn"
                          onClick={() => deactivateItem(item)}
                        >
                          Deactivate
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default JuniorEatsMainMenuPage;
